import { useEffect, useMemo, useRef, useState } from 'react';
import { crmStore, useCrm } from '../store';
import { Card } from '../ui';
import { formatDateFR, formatEUR } from '../utils';
import { AlertTriangle, Bell, Mail, Phone, CheckCircle2, Clock, Receipt, Circle, ChevronDown, ChevronRight, Undo2, History, BellPlus, AlarmClock, Pencil, Trash2, RotateCcw, ListPlus, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { translateError } from '@/crm/utils/translateError';
import type { Facture } from '../types';
import { withDemoFlag, demoFilterValue } from '../demoMode';
import { useAuth } from '../auth/AuthContext';
import HistoryRelancesModal, { type HistoryEntry } from './HistoryRelancesModal';
import ReminderModal, { type ReminderDraft } from './ReminderModal';
import { RelanceStats, RelanceFilterChips, type Tone } from './RelancePageLayout';
import { EcheancierEditor, getEcheances, type EchRow } from './EcheancierEditor';
import { fetchEcheancierFromProp } from '../utils/echeancierFromProp';


type FactureReminder = {
  id: string; // uuid prestations_taches
  factureId: string;
  date: string;
  note: string;
  done: boolean;
};

interface RelanceRow {
  id: string;
  facture_id: string | null;
  date_relance: string;
  type: string;
  note: string | null;
  cloturee: boolean;
}

type Bucket = 'aVenir' | 'j15' | 'j30' | 'j45' | 'j60+';

const BUCKETS: { key: Bucket; label: string; color: string; range: string; tone: Tone }[] = [
  { key: 'aVenir', label: 'À venir', color: 'text-slate-400 bg-slate-800/40 border-slate-700', range: 'Échéance future', tone: 'slate' },
  { key: 'j15', label: 'J+15', color: 'text-amber-400 bg-amber-500/10 border-amber-500/30', range: '1 → 15 j de retard', tone: 'amber' },
  { key: 'j30', label: 'J+30', color: 'text-orange-400 bg-orange-500/10 border-orange-500/30', range: '16 → 30 j de retard', tone: 'orange' },
  { key: 'j45', label: 'J+45', color: 'text-red-400 bg-red-500/10 border-red-500/30', range: '31 → 45 j de retard', tone: 'red' },
  { key: 'j60+', label: 'J+60+', color: 'text-rose-500 bg-rose-500/15 border-rose-500/40', range: '> 45 j — contentieux', tone: 'rose' },
];

function ttcOf(f: Facture): number {
  return f.lignes.reduce((s, l) => s + l.quantite * l.prixUnitaireHT * (1 + l.tvaPct / 100), 0) * (1 - (f.remisePct || 0) / 100);
}

function jOfRetard(dateEcheance: string): number {
  if (!dateEcheance) return 0;
  const d = new Date(dateEcheance).getTime();
  return Math.floor((Date.now() - d) / 86400000);
}

function bucketOf(j: number): Bucket {
  if (j < 0) return 'aVenir';
  if (j <= 15) return 'j15';
  if (j <= 30) return 'j30';
  if (j <= 45) return 'j45';
  return 'j60+';
}

export default function RelancesFacturesSection() {
  const data = useCrm();
  const { user } = useAuth();
  const [filter, setFilter] = useState<Bucket | 'all'>('all');
  const [relances, setRelances] = useState<RelanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Historique unifié (table relances_historique partagée avec Relances Client), indexé par facture_id
  const [historyMap, setHistoryMap] = useState<Record<string, HistoryEntry[]>>({});
  const [historyModal, setHistoryModal] = useState<{ factureId: string } | null>(null);
  // Rappels personnalisés (= prestations_taches, catégorie « Relance facture »), indexés par facture_id
  const [remindersMap, setRemindersMap] = useState<Record<string, FactureReminder[]>>({});
  const [reminderModal, setReminderModal] = useState<{ facture: Facture; initial?: ReminderDraft } | null>(null);
  const [editorModal, setEditorModal] = useState<Facture | null>(null);

  const todayIso = new Date().toISOString().slice(0, 10);

  const loadRelances = async () => {
    const { data: rows } = await (supabase.from('relances' as any) as any)
      .select('*')
      .eq('type', 'facture')
      .order('date_relance', { ascending: false });
    setRelances((rows as any[]) || []);
  };

  const loadHistorique = async () => {
    const { data: rows } = await (supabase.from('relances_historique' as any) as any)
      .select('id, facture_id, date, note, auteur_email, source')
      .eq('source', 'relance_facture')
      .not('facture_id', 'is', null)
      .order('date', { ascending: false });
    const map: Record<string, HistoryEntry[]> = {};
    for (const r of ((rows as any[]) || [])) {
      const key = r.facture_id as string;
      (map[key] ||= []).push({
        id: r.id,
        date: r.date,
        note: r.note || undefined,
        auteur: r.auteur_email || undefined,
        source: r.source || 'relance_facture',
      });
    }
    setHistoryMap(map);
  };

  const loadReminders = async () => {
    const { data: rows } = await supabase
      .from('prestations_taches')
      .select('id, libelle, notes, date_limite, fait, is_demo')
      .eq('categorie', 'Relance facture')
      .eq('is_demo', demoFilterValue())
      .order('date_limite', { ascending: true });
    const map: Record<string, FactureReminder[]> = {};
    for (const r of ((rows as any[]) || [])) {
      // Le `notes` contient une ligne "facture:<uuid>" + le commentaire saisi (séparés par \n).
      const notes = r.notes || '';
      const m = notes.match(/^facture:([0-9a-f-]{36})\n?/i);
      if (!m) continue;
      const factureId = m[1];
      const userNote = notes.slice(m[0].length).trim() || r.libelle.replace(/^Rappel facture[^:]*:\s*/i, '');
      (map[factureId] ||= []).push({
        id: r.id,
        factureId,
        date: r.date_limite || '',
        note: userNote,
        done: !!r.fait,
      });
    }
    setRemindersMap(map);
  };

  useEffect(() => {
    loadRelances();
    loadHistorique();
    loadReminders();
  }, []);

  const hydratedRef = useRef<Set<string>>(new Set<string>());
  useEffect(() => {
    (async () => {
      for (const f of data.factures || []) {
        if (['Payée', 'Annulée', 'Brouillon'].includes(f.statut)) continue;
        if ((f.echeancier || []).length > 0) continue;
        if (!f.prestationId) continue;
        if (hydratedRef.current.has(f.id)) continue;
        hydratedRef.current.add(f.id);
        const rows = await fetchEcheancierFromProp(f);
        if (rows.length > 1) {
          await crmStore.upsertFacture({
            ...f,
            echeancier: rows.map((r) => ({ label: r.label, date: r.date, montant: r.montant })),
          });
        }
      }
    })();
  }, [data.factures]);

  // Factures impayées : non Payée / non Annulée / non Brouillon, avec un dû > 0
  const facturesImpayees = useMemo(() => {
    return (data.factures || [])
      .filter((f) => !['Payée', 'Annulée', 'Brouillon'].includes(f.statut))
      .map((f) => {
        const ttc = ttcOf(f);
        const du = Math.max(0, ttc - (f.montantPaye || 0));
        // Date de référence = prochaine échéance non payée si échéancier existe
        const ech = (f.echeancier || []).filter((e) => e.date);
        const nextUnpaid = ech
          .filter((e) => !e.paid)
          .sort((a, b) => (a.date || '').localeCompare(b.date || ''))[0];
        const refDate = nextUnpaid?.date || f.dateEcheance;
        const j = jOfRetard(refDate);
        return { f, ttc, du, j, bucket: bucketOf(j), refDate, nextUnpaid };
      })
      .filter((r) => r.du > 0.01)
      .sort((a, b) => b.j - a.j);
  }, [data.factures]);

  const counts = useMemo(() => {
    const m: Record<Bucket | 'all' | 'totalDue', number> = {
      all: facturesImpayees.length,
      aVenir: 0, j15: 0, j30: 0, j45: 0, 'j60+': 0,
      totalDue: 0,
    };
    facturesImpayees.forEach((r) => { m[r.bucket]++; m.totalDue += r.du; });
    return m;
  }, [facturesImpayees]);

  const filtered = filter === 'all' ? facturesImpayees : facturesImpayees.filter((r) => r.bucket === filter);

  const lastRelanceMap = useMemo(() => {
    const m = new Map<string, RelanceRow>();
    relances.forEach((r) => {
      if (!r.facture_id) return;
      if (!m.has(r.facture_id)) m.set(r.facture_id, r);
    });
    return m;
  }, [relances]);

  // Résout l'UUID DB d'une prestation depuis son CRM id (numero) ou un UUID direct.
  const resolvePrestationUuid = async (idOrNumero: string | null | undefined): Promise<string | null> => {
    if (!idOrNumero) return null;
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrNumero);
    if (isUuid) return idOrNumero;
    const { data: row } = await supabase.from('prestations').select('id').eq('numero', idOrNumero).maybeSingle();
    return (row?.id as string) || null;
  };

  // Insertion partagée dans relances_historique (table commune à Relances Client & Factures).
  const insertHistorique = async (factureId: string, prestationId: string | null | undefined, note: string) => {
    const prestUuid = await resolvePrestationUuid(prestationId);
    if (!prestUuid) return; // historique partagé requiert toujours une prestation
    await (supabase.from('relances_historique' as any) as any).insert({
      prestation_id: prestUuid,
      facture_id: factureId,
      date: new Date().toISOString().slice(0, 10),
      note: note || null,
      auteur_user_id: user?.id ?? null,
      auteur_email: user?.email ?? null,
      source: 'relance_facture',
    });
  };

  // ─── Rappels personnalisés sur facture (= prestations_taches catégorie "Relance facture") ───
  const upsertReminder = async (facture: Facture, draft: ReminderDraft) => {
    const prestUuid = await resolvePrestationUuid(facture.prestationId);
    if (!prestUuid) {
      toast.error('Facture sans prestation liée — impossible de poser un rappel.');
      return;
    }
    const libelle = `Rappel facture ${facture.numero} : ${draft.note || 'rappel programmé'}`;
    const notes = `facture:${facture.id}\n${draft.note || ''}`;
    const existing = remindersMap[facture.id]?.find((r) => r.id === draft.id);
    if (existing) {
      await supabase.from('prestations_taches').update({
        libelle, notes, date_limite: draft.date, fait: !!draft.done,
      }).eq('id', existing.id);
      toast.success('Rappel mis à jour');
    } else {
      const { error } = await supabase.from('prestations_taches').insert(withDemoFlag({
        prestation_id: prestUuid,
        libelle,
        notes,
        categorie: 'Relance facture',
        responsable: user?.email || null,
        date_limite: draft.date,
        fait: !!draft.done,
        created_by: user?.id ?? null,
      }));
      if (error) { toast.error('Erreur : ' + translateError(error.message)); return; }
      toast.success('Rappel ajouté');
    }
    loadReminders();
  };

  const toggleReminderDone = async (rem: FactureReminder) => {
    await supabase.from('prestations_taches').update({ fait: !rem.done }).eq('id', rem.id);
    loadReminders();
  };

  const deleteReminder = async (rem: FactureReminder) => {
    await supabase.from('prestations_taches').delete().eq('id', rem.id);
    toast.success('Rappel supprimé');
    loadReminders();
  };


  const marquerRelance = async (facture: Facture, note: string) => {
    setLoading(true);
    const { error } = await (supabase.from('relances' as any) as any).insert(withDemoFlag({
      facture_id: facture.id,
      type: 'facture',
      date_relance: new Date().toISOString().slice(0, 10),
      note,
      cloturee: false,
    }));
    if (!error) {
      await insertHistorique(facture.id, facture.prestationId, note);
    }
    setLoading(false);
    if (error) {
      toast.error('Erreur : ' + translateError(error.message));
      return;
    }
    toast.success('Relance enregistrée');
    loadRelances();
    loadHistorique();
  };

  // Persiste un échéancier complet (depuis l'éditeur) et met à jour le statut facture
  const persistEcheancier = async (f: Facture, newEch: EchRow[]) => {
    const totalPaye = newEch.filter((e) => e.paid).reduce((s, e) => s + (e.montant || 0), 0);
    const allPaid = newEch.length > 0 && newEch.every((e) => e.paid);
    const anyPaid = newEch.some((e) => e.paid);
    const updated: Facture = {
      ...f,
      echeancier: newEch.map((e) => ({ label: e.label, date: e.date, montant: e.montant, paid: e.paid, paid_at: e.paid_at })),
      montantPaye: totalPaye,
      statut: allPaid ? 'Payée' : (anyPaid ? 'Partiellement payée' : (f.statut === 'Payée' || f.statut === 'Partiellement payée' ? 'Émise' : f.statut)),
      datePaiement: allPaid ? new Date().toISOString().slice(0, 10) : (f.datePaiement || ''),
    };
    await crmStore.upsertFacture(updated);
    toast.success('Échéancier mis à jour');
    await crmStore.reload();
  };

  // Marque/démarque une échéance comme payée et met à jour facture + prestation
  const toggleEcheancePayee = async (f: Facture, index: number, payee: boolean) => {
    setLoading(true);
    try {
      const ech = [...(f.echeancier || [])];
      if (!ech[index]) return;
      ech[index] = {
        ...ech[index],
        paid: payee,
        paid_at: payee ? new Date().toISOString().slice(0, 10) : undefined,
      };
      const ttc = ttcOf(f);
      const montantPaye = ech.reduce((s, e) => s + (e.paid ? Number(e.montant || 0) : 0), 0);
      const tousPayes = ech.length > 0 && ech.every((e) => e.paid);
      // Fallback : si pas d'échéancier détaillé mais l'utilisateur marque tout payé
      const soldee = tousPayes || montantPaye >= ttc - 0.01;

      const updated: Facture = {
        ...f,
        echeancier: ech,
        montantPaye,
        statut: soldee ? 'Payée' : montantPaye > 0 ? 'Partiellement payée' : f.statut,
        datePaiement: soldee ? new Date().toISOString().slice(0, 10) : f.datePaiement,
      };
      await crmStore.upsertFacture(updated);

      // Si soldée → presta "Payé & Livré" si event passé (livré), sinon "Payée" ; archivage auto
      let livreEtPaye = false;
      if (soldee && f.prestationId) {
        const prest = (data.prest || []).find((p) => p.id === f.prestationId);
        if (prest) {
          const livre = !!prest.dateEvent && prest.dateEvent < new Date().toISOString().slice(0, 10);
          const newStatut = livre ? 'Payé & Livré' : 'Payée';
          if (prest.statut !== newStatut) {
            await crmStore.upsertPrest({ ...prest, statut: newStatut });
          }
          if (livre && !prest.archivedAt) {
            await crmStore.archivePrest(prest.id);
            livreEtPaye = true;
          }
        }
      }

      const noteHist = payee
        ? `Échéance « ${ech[index].label || formatDateFR(ech[index].date || '')} » encaissée`
        : `Échéance « ${ech[index].label || formatDateFR(ech[index].date || '')} » rouverte`;

      // Double écriture le temps de la migration : table legacy "relances" + historique unifié
      await (supabase.from('relances' as any) as any).insert(withDemoFlag({
        facture_id: f.id,
        type: 'facture',
        date_relance: new Date().toISOString().slice(0, 10),
        note: noteHist,
        cloturee: soldee,
      }));
      await insertHistorique(f.id, f.prestationId, soldee ? `${noteHist} — facture soldée` : noteHist);

      toast.success(
        livreEtPaye ? 'Facture soldée — prestation « Payé & Livré » et archivée'
        : soldee ? 'Facture soldée — prestation marquée Payée'
        : (payee ? 'Échéance encaissée' : 'Échéance rouverte')
      );
      loadRelances();
      loadHistorique();
      await crmStore.reload();
    } catch (e: any) {
      toast.error('Erreur : ' + translateError(e?.message || String(e)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>


      {/* Stats */}
      <RelanceStats items={[
        { label: 'Total impayées', value: counts.all, tone: 'amber' },
        { label: 'Montant dû', value: formatEUR(counts.totalDue), tone: 'rose' },
        ...BUCKETS.filter((b) => b.key !== 'aVenir').map((b) => ({
          label: b.label, value: counts[b.key], tone: b.tone,
        })),
      ]} />

      {/* Filtres */}
      <RelanceFilterChips items={[
        { key: 'all', label: 'Toutes', count: counts.all, active: filter === 'all', onClick: () => setFilter('all'), tone: 'amber' },
        ...BUCKETS.map((b) => ({
          key: b.key, label: b.label, count: counts[b.key],
          active: filter === b.key, onClick: () => setFilter(b.key), tone: b.tone,
        })),
      ]} />

      {/* Liste */}
      <div className="space-y-2">
        {filtered.map(({ f, ttc, du, j, bucket, refDate, nextUnpaid }) => {
          const last = lastRelanceMap.get(f.id);
          const bConf = BUCKETS.find((x) => x.key === bucket)!;
          const ech = f.echeancier || [];
          const hasEch = ech.length > 0;
          // Affiche toujours au moins une ligne (synthetic « Solde ») pour pouvoir cocher
          const displayEch = hasEch ? ech.map((e, i) => ({ ...e, _idx: i, _synthetic: false })) : [{
            label: 'Solde',
            date: f.dateEcheance,
            montant: Math.max(0, ttc - (f.montantPaye || 0)),
            paid: false,
            _idx: 0,
            _synthetic: true,
          } as any];
          const isExpanded = expanded[f.id] ?? true;
          // Quand on coche une ligne synthétique, on matérialise d'abord l'échéancier
          const toggleLine = (line: any) => {
            if (line._synthetic) {
              toggleEcheancePayee(
                { ...f, echeancier: [{ label: line.label, date: line.date, montant: line.montant }] },
                0,
                !line.paid,
              );
            } else {
              toggleEcheancePayee(f, line._idx, !line.paid);
            }
          };
          return (
            <Card key={f.id} className="p-4">
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 border ${bConf.color}`}>
                  <Receipt size={16} strokeWidth={1.5} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-slate-200 font-medium">{f.numero}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${bConf.color}`}>
                      {bucket === 'aVenir' ? 'Échéance future' : `${bConf.label} · ${j}j de retard`}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-slate-800 text-slate-300 rounded">{f.statut}</span>
                    <button
                      type="button"
                      onClick={() => setExpanded((m) => ({ ...m, [f.id]: !isExpanded }))}
                      className="ml-1 text-[10px] text-slate-400 hover:text-slate-200 flex items-center gap-0.5"
                    >
                      {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      {hasEch
                        ? <>Échéancier ({ech.filter((e) => (e as any).paid).length}/{ech.length})</>
                        : <>Échéancier (1 ligne)</>}
                    </button>
                  </div>
                  <div className="text-sm text-slate-300 mt-1">{f.clientNom}</div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Clock size={11} />
                      {nextUnpaid
                        ? <>Prochaine échéance : {formatDateFR(refDate)}{nextUnpaid.label ? ` · ${nextUnpaid.label}` : ''}</>
                        : <>Échue le {formatDateFR(refDate)}</>}
                    </span>
                    {f.clientEmail && <span className="flex items-center gap-1"><Mail size={11} /> {f.clientEmail}</span>}
                    {f.clientTel && <span className="flex items-center gap-1"><Phone size={11} /> {f.clientTel}</span>}
                  </div>
                  {last && (
                    <div className="text-[10px] text-emerald-400 mt-1.5 flex items-center gap-1">
                      <CheckCircle2 size={11} /> Dernière action : {formatDateFR(last.date_relance)} {last.note ? `— ${last.note}` : ''}
                    </div>
                  )}

                  {isExpanded && (
                    <div className="mt-3 border-t border-slate-800 pt-3 space-y-1.5">
                      {!hasEch && (
                        <div className="text-[10px] text-slate-500 italic mb-1">
                          Aucun échéancier configuré — coche le « Solde » pour solder, ou clique sur « Configurer les traites » pour découper en acomptes/traites.
                        </div>
                      )}
                      {displayEch.map((e, i) => {
                        const due = e.date ? jOfRetard(e.date) : 0;
                        const enRetard = !e.paid && due > 0;
                        return (
                          <div key={i} className="flex items-center gap-2 text-xs group">
                            <button
                              type="button"
                              disabled={loading}
                              onClick={() => toggleLine(e)}
                              title={e.paid ? 'Annuler le règlement' : 'Marquer comme payée'}
                              className="flex items-center gap-2 flex-1 min-w-0 text-left rounded px-1 -mx-1 py-0.5 hover:bg-slate-800/40 disabled:opacity-50"
                            >
                              {e.paid ? (
                                <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
                              ) : (
                                <Circle size={14} className={`${enRetard ? 'text-rose-400' : 'text-slate-500'} shrink-0 group-hover:text-emerald-300`} />
                              )}
                              <span className={`flex-1 truncate ${e.paid ? 'text-slate-500 line-through' : 'text-slate-200'}`}>
                                {e.label || `Échéance ${i + 1}`}
                              </span>
                              <span className={`text-slate-400 ${e.paid ? 'line-through' : ''}`}>
                                {e.date ? formatDateFR(e.date) : '—'}
                              </span>
                              {enRetard && (
                                <span className="text-[10px] px-1 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30">
                                  +{due}j
                                </span>
                              )}
                              <span className={`w-24 text-right tabular-nums ${e.paid ? 'text-emerald-400' : 'text-slate-200'}`}>
                                {formatEUR(Number(e.montant || 0))}
                              </span>
                            </button>
                            <button
                              type="button"
                              disabled={loading}
                              onClick={() => toggleLine(e)}
                              className={`px-2 py-0.5 rounded-md text-[10px] flex items-center gap-1 border disabled:opacity-50 ${
                                e.paid
                                  ? 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                                  : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25'
                              }`}
                            >
                              {e.paid ? <><Undo2 size={10} /> Annuler</> : <><CheckCircle2 size={10} /> Marquer payée</>}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="text-right">
                    <div className="text-xs text-slate-500">TTC : {formatEUR(ttc)}</div>
                    <div className="text-base font-semibold text-rose-400">{formatEUR(du)} dû</div>
                    {f.montantPaye > 0 && (
                      <div className="text-[10px] text-emerald-400">{formatEUR(f.montantPaye)} encaissé</div>
                    )}
                  </div>
                  <div className="flex gap-1.5 flex-wrap justify-end">
                    <button
                      onClick={() => setEditorModal(f)}
                      className="px-2.5 py-1 rounded-md text-[11px] flex items-center gap-1 bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25"
                      title={hasEch ? 'Modifier les traites' : 'Configurer acomptes / traites'}
                    >
                      <ListPlus size={11} strokeWidth={1.5} />
                      {hasEch ? 'Modifier les traites' : 'Configurer les traites'}
                    </button>
                    {(() => {
                      const n = historyMap[f.id]?.length ?? 0;
                      return (
                        <button
                          onClick={() => setHistoryModal({ factureId: f.id })}
                          className="px-2.5 py-1 rounded-md text-[11px] flex items-center gap-1 bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700"
                        >
                          <History size={11} strokeWidth={1.5} /> Historique{n > 0 ? ` (${n})` : ''}
                        </button>
                      );
                    })()}
                    <button
                      onClick={() => setReminderModal({ facture: f })}
                      className="px-2.5 py-1 rounded-md text-[11px] flex items-center gap-1 bg-violet-500/15 text-violet-300 border border-violet-500/40 hover:bg-violet-500/25"
                    >
                      <BellPlus size={11} strokeWidth={1.5} /> Rappel
                    </button>
                    <button
                      disabled={loading}
                      onClick={() => marquerRelance(f, 'Relance email')}
                      className="px-2.5 py-1 rounded-md text-[11px] flex items-center gap-1 bg-sky-500/15 text-sky-300 border border-sky-500/40 hover:bg-sky-500/25 disabled:opacity-50"
                    >
                      <Mail size={11} /> Email
                    </button>
                    <button
                      disabled={loading}
                      onClick={() => marquerRelance(f, 'Relance téléphone')}
                      className="px-2.5 py-1 rounded-md text-[11px] flex items-center gap-1 bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25 disabled:opacity-50"
                    >
                      <Phone size={11} /> Tél
                    </button>
                    <button
                      disabled={loading}
                      onClick={() => marquerRelance(f, 'Mise en demeure')}
                      className="px-2.5 py-1 rounded-md text-[11px] flex items-center gap-1 bg-rose-500/15 text-rose-300 border border-rose-500/40 hover:bg-rose-500/25 disabled:opacity-50"
                    >
                      <Bell size={11} /> Mise en demeure
                    </button>
                  </div>
                </div>
              </div>
              {(remindersMap[f.id]?.length ?? 0) > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-800 space-y-1.5">
                  {remindersMap[f.id]!.map((r) => {
                    const isDue = !r.done && r.date && r.date <= todayIso;
                    return (
                      <div
                        key={r.id}
                        className={`flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-md border ${
                          r.done
                            ? 'border-slate-800 bg-slate-900/40 text-slate-500 line-through'
                            : isDue
                            ? 'border-violet-500/40 bg-violet-500/10 text-violet-200'
                            : 'border-slate-800 bg-slate-900/60 text-slate-300'
                        }`}
                      >
                        <AlarmClock size={12} strokeWidth={1.5} className="shrink-0" />
                        <span className="font-medium whitespace-nowrap">{r.date ? formatDateFR(r.date) : '—'}</span>
                        <span className="opacity-50">·</span>
                        <span className="flex-1 truncate">{r.note || 'Rappel'}</span>
                        <button onClick={() => toggleReminderDone(r)} className="p-1 rounded hover:bg-slate-800 text-emerald-400" title={r.done ? 'Rouvrir' : 'Marquer comme fait'}>
                          {r.done ? <RotateCcw size={11} strokeWidth={1.5} /> : <CheckCircle2 size={11} strokeWidth={1.5} />}
                        </button>
                        <button onClick={() => setReminderModal({ facture: f, initial: { id: r.id, date: r.date, note: r.note, done: r.done } })} className="p-1 rounded hover:bg-slate-800 text-slate-400" title="Modifier">
                          <Pencil size={11} strokeWidth={1.5} />
                        </button>
                        <button onClick={() => deleteReminder(r)} className="p-1 rounded hover:bg-slate-800 text-rose-400" title="Supprimer">
                          <Trash2 size={11} strokeWidth={1.5} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
        {!filtered.length && (
          <Card className="p-12 text-center text-slate-500">
            <CheckCircle2 size={32} className="mx-auto mb-3 text-emerald-400/60" strokeWidth={1.5} />
            Aucune facture impayée dans ce filtre.
          </Card>
        )}
      </div>

      {historyModal && (() => {
        const f = facturesImpayees.find((r) => r.f.id === historyModal.factureId)?.f
          || (data.factures || []).find((x) => x.id === historyModal.factureId);
        return (
          <HistoryRelancesModal
            subtitle={f ? `${f.numero} — ${f.clientNom}` : undefined}
            entries={historyMap[historyModal.factureId] || []}
            onClose={() => setHistoryModal(null)}
          />
        );
      })()}

      {reminderModal && (
        <ReminderModal
          initial={reminderModal.initial}
          subtitle={`${reminderModal.facture.numero} — ${reminderModal.facture.clientNom}`}
          onClose={() => setReminderModal(null)}
          onSave={async (draft) => {
            await upsertReminder(reminderModal.facture, draft);
            setReminderModal(null);
          }}
        />
      )}

      {editorModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditorModal(null)}>
          <div className="w-full max-w-3xl bg-slate-900 border border-slate-700 rounded-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
              <div>
                <div className="text-sm font-semibold text-slate-100">Configurer les traites</div>
                <div className="text-xs text-slate-500">{editorModal.numero} — {editorModal.clientNom}</div>
              </div>
              <button onClick={() => setEditorModal(null)} className="p-1 text-slate-400 hover:text-slate-200">
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
            <EcheancierEditor
              facture={editorModal}
              ech={getEcheances(editorModal)}
              onSave={async (newEch) => {
                try {
                  await persistEcheancier(editorModal, newEch);
                  setEditorModal(null);
                } catch (e: any) {
                  toast.error('Erreur : ' + translateError(e?.message || String(e)));
                }
              }}
              onCancel={() => setEditorModal(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
